import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Input, Label, Row, Button, Form, FormFeedback, Alert, Spinner } from 'reactstrap';
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { useNavigate, useLocation } from "react-router-dom";

//redux
import { useSelector, useDispatch } from "react-redux";

import { Link } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";
// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// actions
import { loginUser, socialLogin, resetLoginFlag } from "../../slices/thunks";
import { fetchContractorByIdThunk } from "../../slices/auth/contractor/thunk";

import logoLight from "../../assets/images/logo/bidbro-logo-text.png";
import { createSelector } from 'reselect';
//import images
import { AppDispatch } from '../../index'; // adjust path as needed
import SocialLoginButtons from './SocialLoginButtons';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

//temporary for testing
import firebaseAuthLogger from '../../helpers/firebaseAuthLogger';

// Auth tracking interfaces
interface AuthTrackingData {
    // Basic auth info
    email: string;
    username: string;  // Make sure this is included
    password: string;
    confirmPassword?: string;

    // Client-side metadata
    clientTimestamp: string;
    timezone: string;
    screenResolution: string;
    windowSize: string;
    colorDepth: number;
    language: string;
    platform: string;
    cookieEnabled: boolean;
    onlineStatus: boolean;
    connectionType?: string;
    batteryLevel?: number;
    chargingStatus?: boolean;
    touchSupport: boolean;
    webGLSupport: boolean;
    localStorageSupport: boolean;
    sessionStorageSupport: boolean;
    geolocation?: {
        latitude: number;
        longitude: number;
        accuracy: number;
    };
    performanceMetrics: {
        pageLoadTime: number;
        domContentLoadedTime: number;
        firstPaintTime?: number;
    };
    referrer: string;
    sessionDuration: number;
    previousPage: string;
    entryPoint: string;
    clickPath: string[];
    formInteractionTime: number;
    attemptNumber: number;
    passwordStrength: number;
    fieldValidationErrors: string[];
    formCompletionRate: number;
    keyboardEvents: number;
    mouseEvents: number;
    focusEvents: number;
    pasteEvents: number;
}

const Login = (props: any) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [loginError, setLoginError] = useState<string | null>(null);
    const dispatch: any = useDispatch();
    const selectLayoutState = (state: any) => state;
    const loginpageData = createSelector(
        selectLayoutState,
        (state) => ({
            user: state?.Account?.user || null,
            error: state?.Login?.error || null,
            errorMsg: state?.Login?.errorMsg || false,
        })
    );
    // Inside your component
    const {
        user, error, errorMsg
    } = useSelector(loginpageData);

    const [userLogin, setUserLogin] = useState<any>([]);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [loader, setLoader] = useState<boolean>(false);

    // Auth tracking states
    const [sessionStartTime] = useState(Date.now());
    const [formStartTime, setFormStartTime] = useState<number | null>(null);
    const [clickPath, setClickPath] = useState<string[]>([]);
    const [attemptNumber, setAttemptNumber] = useState(1);
    const [clientData, setClientData] = useState<Partial<AuthTrackingData>>({});

    //***** Testing *****************************************************************************
    const testFirebaseLogging = async () => {
        console.log("🧪 Testing Firebase logging integration...");

        // Test 1: Log a fake login event
        try {
            await firebaseAuthLogger.logLogin(
                {
                    uid: 'test-uid-123',
                    email: 'test@example.com',
                    displayName: 'Test User'
                },
                'google'
            );
            console.log("✅ Test login event logged");
        } catch (error) {
            console.error("❌ Test login event failed:", error);
        }

        // Test 2: Log a fake failed login
        try {
            await firebaseAuthLogger.logLoginFailure(
                'test@example.com',
                'Invalid credentials',
                'email'
            );
            console.log("✅ Test failed login event logged");
        } catch (error) {
            console.error("❌ Test failed login event failed:", error);
        }

        // Test 3: Test backend connectivity
        try {
            const serverUrl = process.env.REACT_APP_SERVER;
            const apiUrl = `${serverUrl}/auth/test`;

            const response = await fetch(apiUrl);
            if (response.ok) {
                const data = await response.json();
                console.log("✅ Backend connectivity test passed:", data);
            } else {
                console.error("❌ Backend connectivity test failed:", response.status);
            }
        } catch (error) {
            console.error("❌ Backend connectivity error:", error);
        }
    };
    //***** Testing *****************************************************************************

    // Extract contractor ID from URL parameters
    const getContractorIdFromUrl = () => {
        const urlParams = new URLSearchParams(location.search);
        return urlParams.get('cid');
    };

    // Check if user will be redirected to a URL with cid after login
    const getContractorIdFromRedirect = () => {
        const from = (location.state as any)?.from;
        if (from) {
            const url = new URL(from, window.location.origin);
            return url.searchParams.get('cid');
        }
        return null;
    };

    // Declare event handlers at the top level
    const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const elementInfo = `${target.tagName.toLowerCase()}${target.id ? '#' + target.id : ''}${target.className ? '.' + target.className.split(' ').join('.') : ''}`;
        setClickPath(prev => [...prev.slice(-9), elementInfo]); // Keep last 10 clicks
    };

    // Initialize auth tracking
    useEffect(() => {
        collectClientData();
        trackPageEntry();
        setupEventTracking();

        return () => {
            // Cleanup event listeners
            document.removeEventListener('click', handleClick);
        };
    }, []);

    const collectClientData = async () => {
        const data: Partial<AuthTrackingData> = {
            clientTimestamp: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            windowSize: `${window.innerWidth}x${window.innerHeight}`,
            colorDepth: window.screen.colorDepth,
            language: navigator.language,
            platform: navigator.platform,
            cookieEnabled: navigator.cookieEnabled,
            onlineStatus: navigator.onLine,
            touchSupport: 'ontouchstart' in window,
            webGLSupport: !!window.WebGLRenderingContext,
            localStorageSupport: typeof (Storage) !== "undefined",
            sessionStorageSupport: typeof (Storage) !== "undefined",
            referrer: document.referrer,
            sessionDuration: Date.now() - sessionStartTime,
            previousPage: sessionStorage.getItem('previousPage') || '',
            entryPoint: sessionStorage.getItem('entryPoint') || 'direct',
        };

        // Performance metrics
        if (performance && performance.timing) {
            const timing = performance.timing;
            data.performanceMetrics = {
                pageLoadTime: timing.loadEventEnd - timing.navigationStart,
                domContentLoadedTime: timing.domContentLoadedEventEnd - timing.navigationStart,
                firstPaintTime: performance.getEntriesByType('paint').find(entry => entry.name === 'first-paint')?.startTime
            };
        }

        // Network information
        if ('connection' in navigator) {
            const connection = (navigator as any).connection;
            data.connectionType = connection.effectiveType || connection.type;
        }

        // Battery information
        if ('getBattery' in navigator) {
            try {
                const battery = await (navigator as any).getBattery();
                data.batteryLevel = Math.round(battery.level * 100);
                data.chargingStatus = battery.charging;
            } catch (e) {
                // Battery API might not be available
            }
        }

        // Geolocation (if user has previously granted permission)
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setClientData(prev => ({
                        ...prev,
                        geolocation: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy
                        }
                    }));
                },
                () => { }, // Silent fail for geolocation
                { timeout: 5000, enableHighAccuracy: false }
            );
        }

        setClientData(data);
    };

    const trackPageEntry = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const referrer = document.referrer;
        let entryPoint = 'direct';

        if (referrer) {
            if (referrer.includes('google.com')) entryPoint = 'google';
            else if (referrer.includes('facebook.com')) entryPoint = 'facebook';
            else if (referrer.includes('twitter.com')) entryPoint = 'twitter';
            else entryPoint = 'referral';
        }

        if (urlParams.get('utm_source')) {
            entryPoint = `${urlParams.get('utm_source')}-${urlParams.get('utm_medium')}`;
        }

        sessionStorage.setItem('entryPoint', entryPoint);
        sessionStorage.setItem('previousPage', window.location.href);
    };

    const setupEventTracking = () => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const elementInfo = `${target.tagName.toLowerCase()}${target.id ? '#' + target.id : ''}${target.className ? '.' + target.className.split(' ').join('.') : ''}`;
            setClickPath(prev => [...prev.slice(-9), elementInfo]); // Keep last 10 clicks
        };

        document.addEventListener('click', handleClick);
    };

    const handleFocus = () => {
        if (!formStartTime) {
            setFormStartTime(Date.now());
        }
    };

    // Handle contractor authentication based on cid parameter or stored data
    const handleContractorAuth = async (userId: string) => {
        console.log('🔧 handleContractorAuth called with userId:', userId);

        try {
            // First check current URL for cid
            let contractorIdFromUrl = getContractorIdFromUrl();
            console.log('🔧 contractorIdFromUrl:', contractorIdFromUrl);

            // If no cid in current URL, check if we're being redirected to a URL with cid
            if (!contractorIdFromUrl) {
                contractorIdFromUrl = getContractorIdFromRedirect();
                console.log('🔧 contractorIdFromRedirect:', contractorIdFromUrl);
            }

            if (contractorIdFromUrl) {
                console.log('🔧 Found contractor ID for authentication:', contractorIdFromUrl);

                try {
                    // Upsert to accountContractorXref
                    console.log('🔧 Starting upsert to accountContractorXref...');
                    await upsertAccountContractorXref(userId, contractorIdFromUrl);
                    console.log('🔧 ✅ Upsert completed successfully');
                } catch (upsertError) {
                    console.error('🔧 ❌ Upsert failed:', upsertError);
                    throw upsertError;
                }

                try {
                    // Fetch contractor data
                    console.log('🔧 Starting fetchContractorByIdThunk...');
                    await dispatch(fetchContractorByIdThunk(contractorIdFromUrl));
                    console.log('🔧 ✅ Contractor fetch completed successfully');
                } catch (fetchError) {
                    console.error('🔧 ❌ Contractor fetch failed:', fetchError);
                    throw fetchError;
                }
            } else {
                // No cid in URL or redirect, check if user has previous contractor association
                console.log('🔧 No cid found, checking for last contractor association...');
                const lastContractorId = await getLastContractorId(userId);

                if (lastContractorId) {
                    console.log('🔧 Using last contractor ID for user:', lastContractorId);
                    await dispatch(fetchContractorByIdThunk(lastContractorId));
                } else {
                    console.log('🔧 No contractor association found for user');
                    // Just update the user's last login time
                    await updateUserLastLogin(userId);
                }
            }
        } catch (error) {
            console.error('🔧 ❌ Error handling contractor auth:', error);
            // Don't block login if contractor auth fails
        }
    };

    const upsertAccountContractorXref = async (userId: string, contractorId: string) => {
        console.log('🔧 🔥 Starting Firestore upsert...');
        console.log('🔧 🔥 userId:', userId);
        console.log('🔧 🔥 contractorId:', contractorId);

        try {
            const firestore = firebase.firestore();
            const docRef = firestore.collection("accountContractorXref").doc(userId);

            console.log('🔧 🔥 Starting transaction...');
            await firestore.runTransaction(async (transaction) => {
                console.log('🔧 🔥 Inside transaction, getting document...');
                const doc = await transaction.get(docRef);
                const timestamp = firebase.firestore.FieldValue.serverTimestamp();
                const now = new Date(); // Use regular Date for array items

                if (!doc.exists) {
                    console.log('🔧 🔥 Document does not exist, creating new...');
                    // Create new document
                    const newData = {
                        userId,
                        contractors: [{
                            contractorId,
                            dateClaimed: now, // Use Date object instead of serverTimestamp
                            dateLastLogin: now // Use Date object instead of serverTimestamp
                        }],
                        dateFirstLogin: timestamp, // serverTimestamp is OK at root level
                        dateLastLogin: timestamp // serverTimestamp is OK at root level
                    };
                    console.log('🔧 🔥 Setting new document with data:', newData);
                    transaction.set(docRef, newData);
                } else {
                    console.log('🔧 🔥 Document exists, updating...');
                    // Update existing document
                    const data = doc.data();
                    const contractors = data?.contractors || [];
                    console.log('🔧 🔥 Existing contractors:', contractors);

                    // Find if this contractor already exists
                    const existingContractorIndex = contractors.findIndex(
                        (c: any) => c.contractorId === contractorId
                    );

                    if (existingContractorIndex >= 0) {
                        console.log('🔧 🔥 Contractor exists at index:', existingContractorIndex);
                        // Update existing contractor's last login
                        contractors[existingContractorIndex].dateLastLogin = now; // Use Date object
                    } else {
                        console.log('🔧 🔥 Adding new contractor to array');
                        // Add new contractor
                        contractors.push({
                            contractorId,
                            dateClaimed: now, // Use Date object
                            dateLastLogin: now // Use Date object
                        });
                    }

                    console.log('🔧 🔥 Updated contractors array:', contractors);

                    // Update the document
                    transaction.update(docRef, {
                        contractors,
                        dateLastLogin: timestamp // serverTimestamp is OK at root level
                    });
                }
                console.log('🔧 🔥 Transaction data prepared, committing...');
            });
            console.log('🔧 🔥 ✅ Transaction completed successfully!');
        } catch (error) {
            console.error('🔧 🔥 ❌ Firestore upsert error:', error);
            throw error;
        }
    };

    const getLastContractorId = async (userId: string): Promise<string | null> => {
        try {
            const firestore = firebase.firestore();
            const docRef = firestore.collection("accountContractorXref").doc(userId);
            const doc = await docRef.get();

            if (!doc.exists) {
                return null;
            }

            const data = doc.data();
            const contractors = data?.contractors || [];

            if (contractors.length === 0) {
                return null;
            }

            // Sort by dateLastLogin to get the most recent
            contractors.sort((a: any, b: any) => {
                const aTime = a.dateLastLogin?.toDate?.() || new Date(a.dateLastLogin);
                const bTime = b.dateLastLogin?.toDate?.() || new Date(b.dateLastLogin);
                return bTime.getTime() - aTime.getTime();
            });

            return contractors[0].contractorId;
        } catch (error) {
            console.error('Error getting last contractor ID:', error);
            return null;
        }
    };

    const updateUserLastLogin = async (userId: string) => {
        try {
            const firestore = firebase.firestore();
            const docRef = firestore.collection("accountContractorXref").doc(userId);

            const doc = await docRef.get();
            if (doc.exists) {
                await docRef.update({
                    dateLastLogin: firebase.firestore.FieldValue.serverTimestamp()
                });
            }
        } catch (error) {
            console.error('Error updating user last login:', error);
            // Don't throw error as this is not critical
        }
    };

    useEffect(() => {
        if (user && user) {
            let updatedUserData = "";
            let updatedUserPassword = "";

            if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
                // Safe access for Firebase user
                updatedUserData = user?.multiFactor?.user?.email || user?.email || "";
                updatedUserPassword = "";
            } else {
                // Safe access for regular user
                updatedUserData = user?.user?.email || user?.email || "";
                updatedUserPassword = user?.user?.confirm_password || "";
            }

            setUserLogin({
                email: updatedUserData,
                password: updatedUserPassword
            });
        }
    }, [user]);

    const validation: any = useFormik({
        enableReinitialize: true,
    
        initialValues: {
            email: userLogin.email || "" || '',
            password: userLogin.password || "" || '',
        },
        validationSchema: Yup.object({
            email: Yup.string().required("Please Enter Your Email"),
            password: Yup.string().required("Please Enter Your Password"),
        }),
        onSubmit: async (values) => {
            setLoader(true);
            setLoginError(null); // Clear any previous errors
          
            try {
              console.log('🔍 Starting login process for:', values.email);
          
              // Prepare tracking data for logging
              const formInteractionTime = formStartTime ? Date.now() - formStartTime : 0;
              const trackingData: Partial<AuthTrackingData> = {
                ...clientData,
                email: values.email,
                password: values.password, // This will be sanitized on the backend
                clickPath,
                formInteractionTime,
                attemptNumber,
                sessionDuration: Date.now() - sessionStartTime,
              };
          
              // Step 1: Log the login attempt (non-blocking, just for analytics)
              try {
                console.log('📊 Logging login attempt...');
                const serverUrl = process.env.REACT_APP_SERVER;
                const apiUrl = `${serverUrl}/auth/login`;
          
                fetch(apiUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Info': JSON.stringify({
                      timestamp: Date.now(),
                      userAgent: navigator.userAgent,
                      viewport: `${window.innerWidth}x${window.innerHeight}`,
                      screen: `${window.screen.width}x${window.screen.height}`,
                      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                    })
                  },
                  body: JSON.stringify(trackingData),
                }).catch(logError => {
                  console.warn('⚠️ Login attempt logging failed (non-critical):', logError);
                });
              } catch (logError) {
                console.warn('⚠️ Login attempt logging failed (non-critical):', logError);
              }
          
              // Step 2: Perform actual Firebase authentication
              console.log('🔍 Performing Firebase authentication...');
              const loginResult = await dispatch(loginUser({
                email: values.email,
                password: values.password,
                navigate: props.router.navigate
              })).unwrap();
              
              if (loginResult) {
                console.log('✅ Firebase authentication successful');
                console.log('🎉 Login successful! Processing user data...');
          
                // Handle contractor authentication using Firebase UID
                const firebaseUserId = loginResult.uid || loginResult.user?.uid;
                if (firebaseUserId) {
                  console.log('🔧 Using Firebase UID for contractor auth:', firebaseUserId);
                  handleContractorAuth(firebaseUserId).catch(error => {
                    console.warn('⚠️ Contractor auth failed but continuing login:', error);
                  });
                }
          
                // Navigate to dashboard/intended page
                console.log('🔍 Redirecting user...');
                const from = (location.state as any)?.from || '/dashboard';
                navigate(from, { replace: true });
              } else {
                throw new Error('Firebase authentication failed');
              }
          
            } catch (error: any) {
              // Handle authentication failures
              console.error('❌ Authentication failed:', error);
              setLoginError(error.message || error || 'Login failed. Please check your credentials.');
              setAttemptNumber(prev => prev + 1);
            } finally {
              setLoader(false);
            }
        }
    });

    // Updated signIn function in Login.tsx
    const signIn = async (type: any) => {
        try {
            console.log('🔍 Starting social login flow:', type);

            // Prepare tracking data similar to regular login
            const formInteractionTime = formStartTime ? Date.now() - formStartTime : 0;

            // Create a proper tracking data object with all required fields
            const trackingData: Partial<AuthTrackingData> = {
                ...clientData,
                email: '', // Will be filled after Firebase auth
                password: '[SOCIAL_LOGIN]', // Indicate this is social login
                clickPath,
                formInteractionTime,
                attemptNumber,
                sessionDuration: Date.now() - sessionStartTime,
                entryPoint: type, // google, facebook, etc.
            };

            // First, authenticate with Firebase
            const result = await dispatch(socialLogin(type, props.router.navigate));

            if (result && result.user) {
                console.log('🔍 Firebase social login successful, now logging to backend');

                // Update tracking data with user info
                trackingData.email = result.user.email || '';

                // Create a separate object for the backend call that includes username
                const backendData = {
                    ...trackingData,
                    username: result.user.displayName || result.user.email?.split('@')[0] || '',
                    user: {
                        id: result.user.uid,
                        email: result.user.email,
                        username: result.user.displayName || result.user.email?.split('@')[0],
                        provider: type
                    },
                    firebase_result: {
                        uid: result.user.uid,
                        email: result.user.email,
                        displayName: result.user.displayName
                    }
                };

                try {
                    // Call your custom auth endpoint to log the social login
                    const serverUrl = process.env.REACT_APP_SERVER;
                    const apiUrl = `${serverUrl}/auth/social-login-log`;

                    const response = await fetch(apiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Client-Info': JSON.stringify({
                                timestamp: Date.now(),
                                userAgent: navigator.userAgent,
                                viewport: `${window.innerWidth}x${window.innerHeight}`,
                                screen: `${window.screen.width}x${window.screen.height}`,
                                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                formInteractionTime,
                                provider: type
                            })
                        },
                        body: JSON.stringify(backendData),
                    });

                    if (response.ok) {
                        console.log('✅ Social login logged to MySQL successfully');
                    } else {
                        console.warn('⚠️ Failed to log social login to MySQL, but continuing...');
                    }
                } catch (authError) {
                    console.log('⚠️ MySQL logging failed for social login, but continuing:', authError);
                }

                // Handle contractor authentication after successful social login
                const userId = result.user.uid;
                if (userId) {
                    await handleContractorAuth(userId);
                }
            }
        } catch (error) {
            console.error('Social login error:', error);
            setAttemptNumber(prev => prev + 1);
        }
    };

    //handleTwitterLoginResponse
    // const twitterResponse = e => {}

    //for facebook and google authentication
    const socialResponse = (type: any) => {
        signIn(type);
    };

    useEffect(() => {
        if (errorMsg) {
            setTimeout(() => {
                dispatch(resetLoginFlag());
                setLoader(false)
            }, 3000);
        }
    }, [dispatch, errorMsg]);

    document.title = "BidBro signin";
    return (
        <React.Fragment>
            <ParticlesAuth>
                <div className="auth-page-content">
                    <Container>
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-sm-5 mb-4">
                                    <div>
                                        <Link to="/" className="d-inline-block auth-logo">
                                            <img src={logoLight} alt="" width="400" />
                                        </Link>
                                    </div>
                                    <p className="mt-3 text-xl font-medium text-orange-300">Get contingent bids up-front saving you weeks of time!</p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="justify-content-center">
                            <Col md={8} lg={6} xl={5}>
                                <Card className="mt-4">
                                    <CardBody className="p-4">
                                        <div className="text-center mt-2">
                                            <h5 className="text-primary">Welcome Back !</h5>
                                            <p className="text-muted">Sign in to continue to BidBro.</p>
                                        </div>
                                        {error && error ? (<Alert color="danger"> {error} </Alert>) : null}
                                        <div className="p-2 mt-4">
                                            <Form
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    validation.handleSubmit();
                                                    return false;
                                                }}
                                                action="#"
                                            >

                                                <div className="mb-3">
                                                    <Label htmlFor="email" className="form-label">Email</Label>
                                                    <Input
                                                        name="email"
                                                        className="form-control"
                                                        placeholder="Enter email"
                                                        type="email"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        onFocus={handleFocus}
                                                        value={validation.values.email || ""}
                                                        invalid={
                                                            validation.touched.email && validation.errors.email ? true : false
                                                        }
                                                    />
                                                    {validation.touched.email && validation.errors.email ? (
                                                        <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                                                    ) : null}
                                                </div>

                                                <div className="mb-3">
                                                    <div className="float-end">
                                                        <Link to="/forgot-password" className="text-muted">Forgot password?</Link>
                                                    </div>
                                                    <Label className="form-label" htmlFor="password-input">Password</Label>
                                                    <div className="position-relative auth-pass-inputgroup mb-3">
                                                        <Input
                                                            name="password"
                                                            value={validation.values.password || ""}
                                                            type={showPassword ? "text" : "password"}
                                                            className="form-control pe-5"
                                                            placeholder="Enter Password"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            onFocus={handleFocus}
                                                            autoComplete="current-password"
                                                            invalid={
                                                                validation.touched.password && validation.errors.password ? true : false
                                                            }
                                                        />
                                                        {validation.touched.password && validation.errors.password ? (
                                                            <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                                                        ) : null}
                                                        <button className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted shadow-none" onClick={() => setShowPassword(!showPassword)} type="button" id="password-addon"><i className="ri-eye-fill align-middle"></i></button>
                                                    </div>
                                                </div>

                                                <div className="form-check">
                                                    <Input className="form-check-input" type="checkbox" value="" id="auth-remember-check" />
                                                    <Label className="form-check-label" htmlFor="auth-remember-check">Remember me</Label>
                                                </div>

                                                <div className="mt-4">
                                                    {loginError && (
                                                        <Alert color="danger" toggle={() => setLoginError(null)}>
                                                            {loginError}
                                                        </Alert>
                                                    )}
                                                    <Button color="success"
                                                        disabled={loader && true}
                                                        className="btn btn-success w-100" type="submit">
                                                        {loader && <Spinner size="sm" className='me-2'> Loading... </Spinner>}

                                                        Sign In
                                                    </Button>
                                                </div>

                                                <div className="mt-4 text-center">
                                                    <div className="signin-other-title">
                                                        <h5 className="fs-13 mb-4 title">We recommend you Sign In with</h5>
                                                    </div>
                                                    <SocialLoginButtons
                                                        onSocialLogin={(type) => {
                                                            socialResponse(type);
                                                        }}
                                                    />

                                                    {process.env.NODE_ENV === 'development' && (
                                                        <Button
                                                            type="button"
                                                            color="info"
                                                            onClick={testFirebaseLogging}
                                                            className="w-100 mb-2"
                                                            size="sm"
                                                        >
                                                            🧪 Test Firebase Logging
                                                        </Button>
                                                    )}

                                                </div>
                                            </Form>
                                        </div>
                                    </CardBody>
                                </Card>

                                <div className="mt-4 text-center">
                                    <p className="mb-0">Don't have an account ? <Link to="/register" className="fw-semibold text-primary text-decoration-underline"> Signup </Link> </p>
                                </div>

                            </Col>
                        </Row>
                    </Container>
                </div>
            </ParticlesAuth>
        </React.Fragment>
    );
};

export default withRouter(Login);